import {useFocusManagement} from '@aboutyou/a11y';
import {Fade, Slide} from '@aboutyou/transitions';
import React, {useCallback, useRef} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {Z_INDEXES} from '../../../constants/layers';
import {SPACING} from '../../../constants/sizing';
import {useIOSKeyboardDetection} from '../../../hooks/useIOSKeyboardDetection/useIOSKeyboardDetection';
import {useKeyPress} from '../../../hooks/useKeyPress/useKeyPress';
import {useLockBodyScroll} from '../../../hooks/useLockBodyScroll/useLockBodyScroll';
import {FLYOUT_PORTAL} from '../../../hooks/usePortalElement/portalIds';
import {BlurredOverlay} from '../BlurredOverlay/BlurredOverlay';
import {ContentOpener} from '../ContentPopper/ContentOpener';
import {ContentPopperBaseProps} from '../ContentPopper/ContentPopper';
import {FooterWrapper} from '../ContentPopper/ContentPopperButtons/ContentPopperButtons';
import {ContentPopperHeader} from '../ContentPopper/ContentPopperHeader';
import {
  CONTENT_POPPER_ANIMATION_DURATION_MOUNT,
  CONTENT_POPPER_ANIMATION_DURATION_UNMOUNT,
} from '../ContentPopper/utils/contentPopperAnimationDurations';
import {Icon} from '../Icon/Icon';
import {GenericIconName} from '../Icon/IconName';
import {RenderInPortal} from '../RenderInPortal/RenderInPortal';
import {
  CLICK_OUTSIDE_LAYOUT_SWIPABLE,
  DraggableBottomsheetProps,
  useDraggableBottomsheet,
} from './useDraggableBottomsheet';

export type FlyoutProps = ContentPopperBaseProps & {
  /**
   * use `renderInPortal` flag to render the modal in a separate portal element
   * This is needed in cases where the flyout has z-index issues OR if an action removes
   * the parent element, which would not animate the flyout on close
   */
  renderInPortal?: boolean;
  onUnmount?: VoidFunction;
  fullHeight?: boolean;
  // Percentage of the Flyout content to drag down, before triggering the closing of the Flyout
  draggedPercentageToClose?: DraggableBottomsheetProps['draggedPercentageToClose'];
  hideHeader?: boolean;
};

export const Flyout = ({
  headerContent,
  bodyContent,
  footerContent,
  openerContent,
  onClose,
  onGoBack,
  onOpenerClick,
  open: isOpen,
  isActive,
  'data-testid': dataTestId = 'bottomSheet',
  className,
  renderInPortal,
  onUnmount,
  aboveHeaderElement,
  fullHeight,
  draggedPercentageToClose = 0.5,
  hideCloseButton,
  popoverPriority,
  ...rest
}: FlyoutProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const clickOutsideLayoutRef = useRef<HTMLDivElement | null>(null);

  const {isTopMostPopover, focusManagementRefCallback} = useFocusManagement({
    isOpen,
    priority: popoverPriority,
  });

  const {isKeyboardOpen, keyboardHeight} = useIOSKeyboardDetection();

  useKeyPress({
    key: 'Escape',
    onKeyDown: onClose,
    disabled: !isOpen || !isTopMostPopover,
  });

  const onCloseFlyout = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClose?.();
    },
    [onClose],
  );

  useLockBodyScroll({lockScroll: isOpen});

  const {passRefThrough} = useDraggableBottomsheet({
    onClose,
    draggedPercentageToClose,
    dialogRef,
    headerRef,
  });

  const shouldShowCloseIcon = !hideCloseButton && !onGoBack;

  const flyoutContent = (
    <Fade
      onUnmount={onUnmount}
      mounted={isOpen}
      duration={{
        mount: CONTENT_POPPER_ANIMATION_DURATION_MOUNT,
        unmount: CONTENT_POPPER_ANIMATION_DURATION_UNMOUNT,
      }}
    >
      <BlurredOverlay
        aria-labelledby="bottomSheet"
        aria-modal="true"
        data-testid={dataTestId}
        role="dialog"
        tabIndex={-1}
        className={className}
        $zIndex={Z_INDEXES.FLYOUT_FADE}
      >
        <ClickOutsideLayout
          ref={clickOutsideLayoutRef}
          data-testid="clickOutsideLayout"
          data-swipable={CLICK_OUTSIDE_LAYOUT_SWIPABLE}
          onClick={onCloseFlyout}
        />
        <Slide
          mounted={isOpen}
          direction="up"
          duration={{
            mount: CONTENT_POPPER_ANIMATION_DURATION_MOUNT,
            unmount: CONTENT_POPPER_ANIMATION_DURATION_UNMOUNT + 50,
          }}
        >
          <Wrapper data-full-height={fullHeight}>
            <FloatingElementWrapper
              ref={focusManagementRefCallback()}
              tabIndex={0}
            >
              {aboveHeaderElement}
              <Dialog
                data-testid="flyoutDialog"
                ref={dialogRef}
                $visible={isOpen}
              >
                <Content
                  {...rest}
                  $extraBottomPadding={
                    isKeyboardOpen ? keyboardHeight : undefined
                  }
                >
                  <div ref={passRefThrough}>
                    <HeaderToggleIcon
                      onClick={onCloseFlyout}
                      data-testid="bottomSheetHeaderIcon"
                    >
                      <Icon
                        asset={GenericIconName.Grabber}
                        size="SS"
                        fill={COLORS.Black}
                      />
                    </HeaderToggleIcon>
                    {headerContent && (
                      <ContentPopperHeader
                        data-testid={dataTestId ?? 'bottomSheet'}
                        onGoBack={onGoBack}
                        onClose={
                          shouldShowCloseIcon ? onCloseFlyout : undefined
                        }
                      >
                        {headerContent}
                      </ContentPopperHeader>
                    )}
                  </div>
                  <Body
                    data-testid={`${dataTestId}Body`}
                    $enableTopDivider={Boolean(headerContent)}
                    $enableBottomDivider={Boolean(footerContent)}
                  >
                    {bodyContent}
                  </Body>
                  {footerContent && (
                    <FooterWrapper data-testid={`${dataTestId}Footer`}>
                      {footerContent}
                    </FooterWrapper>
                  )}
                </Content>
              </Dialog>
            </FloatingElementWrapper>
          </Wrapper>
        </Slide>
      </BlurredOverlay>
    </Fade>
  );

  const content = openerContent ? (
    <ContentOpener
      isOpen={isOpen}
      isActive={isActive}
      openerType="single-arrow"
      data-testid={dataTestId ? `${dataTestId}Opener` : 'opener'}
      onClick={onOpenerClick}
    >
      {openerContent}
    </ContentOpener>
  ) : null;

  if (renderInPortal) {
    return (
      <>
        {content}
        {
          <RenderInPortal portalId={FLYOUT_PORTAL}>
            {flyoutContent}
          </RenderInPortal>
        }
      </>
    );
  }

  return (
    <>
      {content}
      {flyoutContent}
    </>
  );
};

const ClickOutsideLayout = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
`;

const Wrapper = styled.div`
  --top-spacing: 4vh;
  position: relative;
  z-index: ${Z_INDEXES.FLYOUT_FADE};
  display: flex;
  align-items: flex-end;
  width: 100%;
  height: calc(100% - var(--top-spacing));
  margin: var(--top-spacing) auto 0;
  pointer-events: none;
  touch-action: none;

  &[data-full-height='true'] {
    --top-spacing: 0px;
  }
`;

const FloatingElementWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  max-height: 100%;

  [data-full-height='true'] & {
    height: 100%;
  }
`;

const Dialog = styled.div<{
  $visible?: boolean;
}>`
  position: relative;
  width: 100%;
  background: ${COLORS.White};
  border-radius: 8px 8px 0 0;
  pointer-events: ${({$visible}) => ($visible ? 'all' : 'none')};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 100%;

  [data-full-height='true'] & {
    height: 100%;
  }
`;

const Content = styled.div<{
  $extraBottomPadding?: number;
}>`
  width: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  // Add extra bottom padding when iOS keyboard is open to prevent content from being covered
  padding-bottom: ${props =>
    props.$extraBottomPadding
      ? Math.min(props.$extraBottomPadding + 20, 200)
      : 0}px;

  [data-full-height='true'] & {
    height: 100%;
  }
`;

const HeaderToggleIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 16px;
`;

export const Body = styled.div<{
  $enableTopDivider: boolean;
  $enableBottomDivider: boolean;
}>`
  padding: ${SPACING['Y.Lg']} ${SPACING['X.Lg']};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  height: 100%;
  border-top: ${props =>
    props.$enableTopDivider
      ? `1px solid  ${COLORS['Outline.Grey']}`
      : 'initial'};
  border-bottom: ${props =>
    props.$enableBottomDivider
      ? `1px solid ${COLORS['Outline.Grey']}`
      : 'initial'};
`;
